import React, { useEffect, useMemo, useState } from "react";
import WinnerReveal from "../../components/live/WinnerReveal";
import { REVEAL_ANIMATIONS } from "../../components/live/reveal/animations";
import { LIVE_YANTRA_IMAGE_URL } from "../../utils/utils";

/**
 * A page for choosing which reveal a game should play, without waiting
 * for a round to reach its last seconds.
 *
 * It fakes the one thing the live screens cannot: the winner arrives a
 * few seconds in, exactly as it does when a round's clock runs out.
 */

const BOARD = [
  ["chatri", "1789984941585.jpg"],
  ["ball", "1789984976228.jpg"],
  ["surya", "1789985413284.jpg"],
  ["divdo", "1789985467624.jpg"],
  ["gaay", "1789985502815.jpg"],
  ["balti", "1789985529270.jpg"],
  ["patang", "1789985309084.jpg"],
  ["bhamrdo", "1789985390655.jpg"],
  ["gulab", "1789985590574.png"],
  ["patangyu", "1789985561154.jpg"],
  ["kabutar", "1789985333558.jpg"],
  ["saslu", "1789985362098.jpg"],
];

const WINNER_AT_SECOND = 6;
const WINDOW_SECONDS = 15;

export default function WinnerRevealPreview() {
  const [animation, setAnimation] = useState("highlight");
  const [runId, setRunId] = useState(0);
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(WINDOW_SECONDS);
  const [winnerIds, setWinnerIds] = useState([]);
  const [winnerIndex, setWinnerIndex] = useState(4);

  const tiles = useMemo(
    () =>
      BOARD.map(([name, img], i) => ({
        id: i + 1,
        name,
        img: `${LIVE_YANTRA_IMAGE_URL}/${img}`,
      })),
    [],
  );

  useEffect(() => {
    if (!running) return undefined;
    setRemaining(WINDOW_SECONDS);
    setWinnerIds([]);
    const started = Date.now();
    const t = setInterval(() => {
      const gone = Math.floor((Date.now() - started) / 1000);
      setRemaining(Math.max(0, WINDOW_SECONDS - gone));
      if (gone >= WINNER_AT_SECOND) {
        setWinnerIds([tiles[winnerIndex].id]);
        clearInterval(t);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [running, runId, tiles, winnerIndex]);

  const play = (slug) => {
    setAnimation(slug);
    setRunning(false);
    setRunId((n) => n + 1);
    setTimeout(() => setRunning(true), 60);
  };

  return (
    <div style={{ padding: 20 }}>
      <h4 className="mb-1">Winner reveal animations</h4>
      <p className="text-muted" style={{ maxWidth: 720 }}>
        Each game plays one of these in its closing seconds - set it per game
        under Settings &rarr; Games. Press one to watch it: the winner
        arrives after {WINNER_AT_SECOND} seconds, the way it does when a
        round's clock reaches zero.
      </p>

      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <span className="fw-bold">Winning yantra:</span>
        <select
          className="form-select"
          style={{ width: 200 }}
          value={winnerIndex}
          onChange={(e) => setWinnerIndex(Number(e.target.value))}
        >
          {tiles.map((t, i) => (
            <option key={t.id} value={i}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="d-flex gap-2 flex-wrap">
        {Object.entries(REVEAL_ANIMATIONS).map(([slug, meta]) => (
          <button
            key={slug}
            type="button"
            className={`btn ${animation === slug ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => play(slug)}
          >
            {meta.title}
            <small className="d-block opacity-75">{slug}</small>
          </button>
        ))}
      </div>

      <WinnerReveal
        visible={running}
        remainingSeconds={remaining}
        betCloseSeconds={WINDOW_SECONDS}
        tiles={tiles}
        winnerIds={winnerIds}
        animation={animation}
        onDone={() => setRunning(false)}
      />
    </div>
  );
}
